// Faithful port of WordPress' wpautop() (wp-includes/formatting.php). The source
// CMS is WordPress, so its raw editor content — bare text with single/blank
// newlines — is meant to be turned into <p>/<br> markup by wpautop. Running the
// same algorithm here renders that content exactly as it does on the WP site.
//
// Two project-specific passes wrap the core algorithm:
//  - PRE: strip stray non-printing control characters (e.g. SOH) the CMS leaves
//    between elements. wpautop doesn't, and — because they aren't whitespace —
//    they survive as rendered text nodes and inflate spacing (notably in flex
//    lists). This is the real cause of the earlier "whitespace between <li>" bug.
//  - POST: wrap <table>s in a horizontally-scrollable container so wide tables
//    don't overflow on narrow screens.
//
// Notes on the PHP→JS translation:
//  - PCRE `\s` does NOT match U+00A0 (nbsp); JS `\s` DOES. Using JS `\s` in the
//    nbsp-sensitive spots (paragraph split, empty-<p> removal, <br> insertion)
//    would wrongly drop intentional &nbsp; spacer paragraphs, so those use
//    `[^\S ]` (whitespace except nbsp) to mirror PCRE.
//  - The lookbehind in wpautop's <br> step is rewritten as a callback so it runs
//    on Safari < 16.4, and so SSR and client produce byte-identical output.

const ALLBLOCKS =
  '(?:table|thead|tfoot|caption|col|colgroup|tbody|tr|td|th|div|dl|dd|dt|ul|ol|li|pre|form|map|area|blockquote|address|style|p|h[1-6]|hr|fieldset|legend|section|article|aside|hgroup|header|footer|nav|figure|figcaption|details|menu|summary)';

// Non-printing C0 control characters (e.g. SOH) and DEL, excluding the real
// whitespace chars tab/LF/VT/FF/CR.
const CONTROL_CHARS_RE = /[\u0000-\u0008\u000E-\u001F\u007F]/g;

// Whitespace except nbsp — mirrors PCRE's `\s` (see note above).
const WS = '[^\\S\\u00A0]';

const blocks = (pattern: string, flags = 'g') => new RegExp(pattern.replace(/ALLBLOCKS/g, ALLBLOCKS), flags);

// Replace newlines inside HTML tags with a placeholder so they aren't treated as
// line breaks by autop (mirrors wp_replace_in_html_tags).
const protectNewlinesInTags = (text: string) =>
  text.replace(/<[^<>]*>/g, (tag) => tag.replace(/\n/g, ' <!-- wpnl --> '));

const wpautop = (input: string, br = true): string => {
  const preTags: Record<string, string> = {};

  if (input.trim() === '') return '';

  // Pad the end so the last block is handled.
  let text = `${input}\n`;

  // <pre> contents must not be touched by autop — swap them for placeholders.
  if (text.includes('<pre')) {
    const parts = text.split('</pre>');
    const last = parts.pop() ?? '';
    text = '';
    parts.forEach((part, i) => {
      const start = part.indexOf('<pre');
      if (start === -1) {
        text += part;
        return;
      }
      const name = `<pre wp-pre-tag-${i}></pre>`;
      preTags[name] = `${part.slice(start)}</pre>`;
      text += part.slice(0, start) + name;
    });
    text += last;
  }

  // Multiple <br>s become two line breaks (i.e. a paragraph break).
  text = text.replace(/<br\s*\/?>\s*<br\s*\/?>/g, '\n\n');

  // Double line break above block opening tags and below block closing tags.
  text = text.replace(blocks('(<ALLBLOCKS[\\s/>])'), '\n\n$1');
  text = text.replace(blocks('(</ALLBLOCKS>)'), '$1\n\n');

  // Double line break after the self-closing <hr>.
  text = text.replace(/(<hr\s*?\/?>)/g, '$1\n\n');

  // Standardize newlines, then protect newlines inside tags.
  text = text.replace(/\r\n|\r/g, '\n');
  text = protectNewlinesInTags(text);

  // Don't autop inside <option>/<object>/<audio>/<video>/<figcaption>.
  if (text.includes('<option')) {
    text = text.replace(/\s*<option/g, '<option').replace(/<\/option>\s*/g, '</option>');
  }
  if (text.includes('</object>')) {
    text = text
      .replace(/(<object[^>]*>)\s*/g, '$1')
      .replace(/\s*<\/object>/g, '</object>')
      .replace(/\s*(<\/?(?:param|embed)[^>]*>)\s*/g, '$1');
  }
  if (text.includes('<source') || text.includes('<track')) {
    text = text
      .replace(/([<[](?:audio|video)[^>\]]*[>\]])\s*/g, '$1')
      .replace(/\s*([<[]\/(?:audio|video)[>\]])/g, '$1')
      .replace(/\s*(<(?:source|track)[^>]*>)\s*/g, '$1');
  }
  if (text.includes('<figcaption')) {
    text = text.replace(/\s*(<figcaption[^>]*>)/g, '$1').replace(/<\/figcaption>\s*/g, '</figcaption>');
  }

  // Collapse 3+ newlines, then split on blank lines and wrap each chunk in <p>.
  text = text.replace(/\n\n+/g, '\n\n');
  const paragraphs = text.split(new RegExp(`\\n${WS}*\\n`)).filter((p) => p !== '');
  text = paragraphs.map((p) => `<p>${p.replace(/^\n+|\n+$/g, '')}</p>\n`).join('');

  // Remove paragraphs of pure whitespace (but keep nbsp-only spacer paragraphs).
  text = text.replace(new RegExp(`<p>${WS}*</p>`, 'g'), '');

  // Add a closing </p> inside div/address/form if missing.
  text = text.replace(/<p>([^<]+)<\/(div|address|form)>/g, '<p>$1</p></$2>');

  // Unwrap block tags that ended up wrapped in (or abutting) a <p>.
  text = text.replace(blocks(`<p>${WS}*(</?ALLBLOCKS[^>]*>)${WS}*</p>`), '$1');
  text = text.replace(/<p>(<li.+?)<\/p>/g, '$1');
  text = text.replace(/<p><blockquote([^>]*)>/gi, '<blockquote$1><p>');
  text = text.replace(/<\/blockquote><\/p>/g, '</p></blockquote>');
  text = text.replace(blocks(`<p>${WS}*(</?ALLBLOCKS[^>]*>)`), '$1');
  text = text.replace(blocks(`(</?ALLBLOCKS[^>]*>)${WS}*</p>`), '$1');

  // Turn the remaining single newlines into <br />.
  if (br) {
    // Preserve newlines inside <script>/<style>/<svg>/<math>.
    text = text.replace(/<(script|style|svg|math)[^>]*>[\s\S]*?<\/\1>/g, (m) =>
      m.replace(/\n/g, '<WPPreserveNewline />')
    );
    text = text.replace(/<br>|<br\/>/g, '<br />');
    // Each newline not already preceded by a <br /> gets one (lookbehind-free).
    text = text.replace(new RegExp(`(<br />)?${WS}*\\n`, 'g'), (m, existing) => (existing ? m : '<br />\n'));
    text = text.replace(/<WPPreserveNewline \/>/g, '\n');
  }

  // Strip <br /> that ended up adjacent to block tags.
  text = text.replace(blocks(`(</?ALLBLOCKS[^>]*>)${WS}*<br />`), '$1');
  text = text.replace(new RegExp(`<br />(${WS}*</?(?:p|li|div|dl|dd|dt|th|pre|td|ul|ol)[^>]*>)`, 'g'), '$1');
  text = text.replace(/\n<\/p>$/g, '</p>');

  // Restore <pre> blocks and the protected in-tag newlines.
  for (const [name, content] of Object.entries(preTags)) {
    text = text.replace(name, () => content);
  }
  if (text.includes('<!-- wpnl -->')) {
    text = text.replace(/ <!-- wpnl --> |<!-- wpnl -->/g, '\n');
  }

  return text;
};

// Wrap tables so they scroll horizontally instead of overflowing narrow screens.
const wrapTables = (html: string): string =>
  html.replace(/<table(?=[\s>])/g, '<div class="table-responsive"><table').replace(/<\/table>/g, '</table></div>');

export function wrapTextNodes(html: string): string {
  if (!html) return html;
  return wrapTables(wpautop(html.replace(CONTROL_CHARS_RE, '')));
}
